/**
 * The Manager Meeting entrance (contexts/20, "The Manager Meeting entrance"): one seven-second
 * clip, with its sound, over the page's area — the header bar and the rail stay uncovered — at 90%
 * opacity, so the meeting shows faintly through it, when a person enters the Manager Meeting from
 * their meetings list. The provider wraps the app shell's `<main>`, above the route outlet, so the
 * navigation it plays over never unmounts it, and draws the overlay beside it inside `.shell`, where
 * the rail's width (collapsed or not) is known; its `<video>` is in the page before the click, because `play()`
 * must run inside the click itself for Chrome to allow sound. The meeting loads underneath and is
 * usable whatever the media does: a refused `play()`, an error, no first frame within the startup
 * timeout, Skip, Escape, a hidden tab or leaving the meeting all close it at once; the clean ending
 * fades into the same frame as a still and the still into the page's own garage background.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useLocation } from 'react-router-dom';
import entranceVideo from '../assets/meeting-entrance/meeting-entrance.mp4';
import entrancePoster from '../assets/meeting-entrance/meeting-entrance-poster.webp';
import garageStill from '../assets/meeting-entrance/meeting-garage-background.webp';
import {
  ENTRANCE_LEAVE_MS,
  ENTRANCE_OVERALL_TIMEOUT_MS,
  ENTRANCE_STARTUP_TIMEOUT_MS,
  canPlayEntrance,
  inFadeWindow,
  isEntranceRoute,
  markEntranceTried,
  shouldPlayEntrance,
  tabStorage,
  type BrowserTraits,
} from '../lib/meetingEntrance.js';
import { useSessionContext } from '../lib/session.js';

interface MeetingEntrance {
  /** Called in the Enter click's own handler: plays when every rule allows, else does nothing. */
  readonly start: (meetingId: number, attending: boolean) => void;
  /** On hover or focus of the link: starts the download, so the click rarely waits. */
  readonly prime: (meetingId: number, attending: boolean) => void;
}

const NONE: MeetingEntrance = { start: () => undefined, prime: () => undefined };
const EntranceContext = createContext<MeetingEntrance>(NONE);

export function useMeetingEntrance(): MeetingEntrance {
  return useContext(EntranceContext);
}

type Phase = 'idle' | 'playing' | 'leaving';

function browserTraits(): BrowserTraits {
  const nav = navigator as Navigator & { userAgentData?: { brands: readonly { brand: string }[]; mobile: boolean } };
  return {
    userAgent: nav.userAgent,
    brands: nav.userAgentData?.brands,
    mobile: nav.userAgentData?.mobile,
    reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  };
}

export function MeetingEntranceProvider({ children }: { readonly children: ReactNode }) {
  const { session } = useSessionContext();
  const userId = session?.user.id ?? '';
  const { pathname } = useLocation();
  // Chrome on a desktop renders the player at all; Safari and the rest never see a <video> or its URL.
  const chrome = useMemo(() => canPlayEntrance({ ...browserTraits(), reducedMotion: false }), []);

  const videoRef = useRef<HTMLVideoElement>(null);
  const skipRef = useRef<HTMLButtonElement>(null);
  const tried = useRef(new Set<string>());
  const timers = useRef<number[]>([]);
  const phaseRef = useRef<Phase>('idle');
  const firstFrame = useRef(false);
  const arrived = useRef(false);
  const [phase, setPhaseState] = useState<Phase>('idle');

  const setPhase = useCallback((next: Phase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  /** Closes the entrance at once: silence first, then the overlay; the meeting is already underneath. */
  const stop = useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.removeAttribute('src');
      v.load(); // drops the buffered clip and its decoder
    }
    firstFrame.current = false;
    arrived.current = false;
    if (phaseRef.current !== 'idle') setPhase('idle');
  }, [setPhase]);

  /** The clean ending: the video fades into the same frame as a still, the still into the page (CSS). */
  const leave = useCallback(() => {
    if (phaseRef.current !== 'playing') return;
    setPhase('leaving');
    timers.current.push(window.setTimeout(stop, ENTRANCE_LEAVE_MS));
  }, [setPhase, stop]);

  const load = (v: HTMLVideoElement) => {
    if (v.getAttribute('src') === null) {
      v.poster = entrancePoster;
      v.src = entranceVideo;
    }
  };

  // Reduced motion is read at the moment, not at mount: the setting can change while the app is open.
  const eligible = (meetingId: number, attending: boolean) =>
    chrome &&
    phaseRef.current === 'idle' &&
    shouldPlayEntrance({ meetingId, attending, userId, canPlay: canPlayEntrance(browserTraits()), store: tabStorage(), tried: tried.current });

  const prime = (meetingId: number, attending: boolean) => {
    const v = videoRef.current;
    if (!v || !eligible(meetingId, attending)) return;
    v.preload = 'auto';
    load(v);
  };

  const start = (meetingId: number, attending: boolean) => {
    const v = videoRef.current;
    if (!v || !eligible(meetingId, attending)) return;
    // Tried from this moment: a remount, a Skip or a failure never replays it in this tab.
    markEntranceTried(userId, meetingId, tabStorage(), tried.current);
    load(v);
    v.muted = false;
    v.currentTime = 0;
    flushSync(() => setPhase('playing')); // the overlay and its poster on screen before the first frame
    // Still inside the click: Chrome's user activation is what lets the sound play.
    v.play().catch(stop);
    later(() => {
      if (!firstFrame.current) stop();
    }, ENTRANCE_STARTUP_TIMEOUT_MS);
    later(stop, ENTRANCE_OVERALL_TIMEOUT_MS);
  };

  const onPlaying = () => {
    firstFrame.current = true;
    // Frame-accurate where Chrome offers it; `timeupdate` (every ~250 ms) is the fallback.
    const v = videoRef.current;
    if (!v || typeof v.requestVideoFrameCallback !== 'function') return;
    const watch = () => {
      if (phaseRef.current !== 'playing' || !videoRef.current) return;
      if (inFadeWindow(videoRef.current.currentTime, videoRef.current.duration)) leave();
      else videoRef.current.requestVideoFrameCallback(watch);
    };
    v.requestVideoFrameCallback(watch);
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (v && inFadeWindow(v.currentTime, v.duration)) leave();
  };

  // Leaving the meeting stops it; its tabs do not. The click runs before the address changes, so it
  // only counts once the meeting's address has been reached.
  useEffect(() => {
    if (phase === 'idle') return;
    if (isEntranceRoute(pathname)) arrived.current = true;
    else if (arrived.current) stop();
  }, [pathname, phase, stop]);

  // Escape, and a hidden tab, close it; Skip takes the focus so Enter or Space also does.
  useEffect(() => {
    if (phase === 'idle') return;
    skipRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stop();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') stop();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [phase, stop]);

  // Another person signing in silences it.
  useEffect(() => stop, [userId, stop]);
  // So does the shell going away (sign-out). The element is held from mount: by the time an
  // unmount's cleanup runs, React has already emptied the ref, and a detached video keeps playing.
  useEffect(() => {
    const v = videoRef.current;
    const pending = timers; // the list is replaced on every stop; read it at cleanup
    return () => {
      pending.current.forEach((t) => window.clearTimeout(t));
      if (!v) return;
      v.pause();
      v.removeAttribute('src');
      v.load();
    };
  }, []);

  const api: MeetingEntrance = { start, prime };
  const active = phase !== 'idle';

  return (
    <EntranceContext.Provider value={chrome ? api : NONE}>
      {children}
      {chrome && (
        // Not modal: the header bar and the rail stay usable, and leaving through the rail closes it.
        <div className={`entrance${active ? ' entrance--active' : ''}${phase === 'leaving' ? ' entrance--leaving' : ''}`} role={active ? 'region' : undefined} aria-label="Manager Meeting entrance">
          {active && <img className="entrance__still" src={garageStill} alt="" />}
          <video ref={videoRef} className="entrance__video" preload="none" playsInline disablePictureInPicture aria-hidden="true" onPlaying={onPlaying} onTimeUpdate={onTimeUpdate} onEnded={leave} onError={stop} />
          {active && (
            <button type="button" ref={skipRef} className="entrance__skip" onClick={stop}>
              Skip
            </button>
          )}
        </div>
      )}
    </EntranceContext.Provider>
  );
}
